<template>
  <div class="pre-nu-call-steps">
    <div v-if="rows.length" class="pre-nu-call-steps__headrow">
      <p class="pre-nu-call-steps__intro">
        {{ introText }}
      </p>
      <q-btn
        class="pre-nu-call-steps__head-btn"
        color="primary"
        size="sm"
        no-caps
        :disable="!allStepsDone"
        @click="onCallNuClick"
      >
        Вызвать ИТР
      </q-btn>
    </div>

    <div v-if="!rows.length" class="pre-nu-call-steps__empty">
      Нет шагов для отображения
    </div>

    <div v-else class="pre-nu-call-steps__table-wrapper tw-overflow-x-auto">
      <table class="pre-nu-call-steps__table tw-w-full tw-text-sm tw-border-collapse">
        <thead>
          <tr class="tw-border-b tw-text-left">
            <th class="tw-py-2 tw-px-2 tw-w-[40px] tw-text-center">№</th>
            <th class="tw-py-2 tw-px-2">Шаг</th>
            <th class="tw-py-2 tw-px-2">Описание</th>
            <th class="tw-py-2 tw-px-2 tw-w-[140px] tw-text-center">Действие</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in rows"
            :key="(row.acceptance_step_code || '') + '_' + idx"
            class="tw-border-b tw-border-grey-4"
            :class="{ 'pre-nu-call-steps__row--done': isStepDone(row) }"
          >
            <td class="tw-py-2 tw-px-2 tw-align-top tw-text-center">
              {{ row.step_order ?? (idx + 1) }}
            </td>
            <td class="tw-py-2 tw-px-2 tw-align-top">
              <div class="pre-nu-call-steps__name">{{ row.name || '—' }}</div>
            </td>
            <td class="tw-py-2 tw-px-2 tw-align-top">
              <span class="pre-nu-call-steps__info">{{ row.info || '' }}</span>
            </td>
            <td class="tw-py-2 tw-px-2 tw-align-top tw-text-center">
              <q-icon
                v-if="isStepDone(row)"
                name="check_circle"
                color="positive"
                size="24px"
              >
                <q-tooltip>Выполнено</q-tooltip>
              </q-icon>
              <template v-else-if="!isCallNuStep(row)">
                <q-btn
                  v-if="isCertificateUrlStep(row)"
                  color="primary"
                  size="sm"
                  no-caps
                  :disable="!getOpenUrl(row)"
                  @click="onUploadClick(row)"
                >
                  Загрузить
                </q-btn>
                <q-btn
                  v-else
                  color="primary"
                  size="sm"
                  no-caps
                  :loading="signLoading"
                  @click="onSignClick(row)"
                >
                  Подписать
                </q-btn>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { signEntranceDocument, validateExecListBeforeOkk } from '@/views/ResidentialSettings/services';
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';
import { useAppStore } from '@/services/store/app';
import { downloadFile } from '@/services';

export default {
  name: 'PreNuCallSteps',
  props: {
    data: { type: Object, required: true },
    closeModal: { type: Function, required: true },
  },
  data() {
    return {
      signLoading: false,
    };
  },
  computed: {
    rows() {
      const arr = this.data?.rows ?? [];
      return Array.isArray(arr) ? arr : [];
    },
    allStepsDone() {
      const list = this.rows;
      if (!list.length) return false;
      return list.every((row) => this.isStepDone(row));
    },
    introText() {
      if (this.allStepsDone) {
        return 'Все шаги до вызова ИТР готовы. Нажмите на кнопку «Вызвать ИТР».';
      }
      return 'Чтобы вызвать ИТР, необходимо выполнить все шаги ниже.';
    },
  },
  methods: {
    getRowStepType(row) {
      const t = row?.type;
      if (t == null || t === '') return '';
      return String(t).trim().toLowerCase();
    },
    isStepDone(row) {
      const v = row?.pre_nu_call_check;
      return v === true || v === 'True' || v === 't' || v === 1 || v === '1';
    },
    isUploadStep(row) {
      return row?.acceptance_step_code === 'ASR_CERTIFICATES_UPLOAD';
    },
    isCertificateUrlStep(row) {
      if (this.getRowStepType(row) === 'certificate_url') return true;
      return this.isUploadStep(row);
    },
    isCallNuStep(row) {
      return this.getRowStepType(row) === 'call_nu';
    },
    isExecListSignStep(row) {
      const stepType = this.getRowStepType(row);
      const stepCode = String(row?.acceptance_step_code || '').trim().toUpperCase();
      return (
        stepType === 'exec_list_contractor' ||
        stepType === 'exec_list_geo' ||
        stepCode === 'BP_EXEC_LIST_SIGN' ||
        stepCode === 'ITR_EXEC_DOCS_SIGN' ||
        stepCode === 'ITR_ASR_AND_EXEC_SIGN' ||
        stepCode === 'SURVEYOR_EXEC_DOCS_REVIEW_SIGN'
      );
    },
    normalizeUrl(raw) {
      if (!raw) return '';
      return String(raw).replace(/\s+/g, '');
    },
    getRowUrl(row) {
      return this.normalizeUrl(row?.url);
    },
    getOpenUrl(row) {
      const t = this.getRowStepType(row);
      if (t === 'certificate_url' && row?.certificate_url != null && row.certificate_url !== '') {
        return this.normalizeUrl(row.certificate_url);
      }
      return this.getRowUrl(row);
    },
    buildAbsoluteUrl(url) {
      if (!url) return '';
      if (/^https?:\/\//i.test(url)) return url;
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const path = url.startsWith('/') ? url : '/' + url;
      return origin + path;
    },
    onUploadClick(row) {
      const url = this.getOpenUrl(row);
      if (!url) return;
      const absoluteUrl = this.buildAbsoluteUrl(url);
      window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    },
    async onCallNuClick() {
      const fn = this.data?.onCallNuContinue;
      if (typeof fn !== 'function') {
        this.showErrorSnackbar('Действие недоступно');
        return;
      }
      this.closeModal();
      await fn();
    },
    pickSignRedirectUrl(payload) {
      if (!payload || typeof payload !== 'object') return '';
      const inner = payload.data !== undefined ? payload.data : payload;
      const block =
        inner && typeof inner === 'object' && inner.data !== undefined ? inner.data : inner;
      if (block && typeof block === 'object') {
        const u = block.redirect_url || block.sign_url;
        if (u) return u;
      }
      if (typeof inner === 'object' && inner) {
        const u2 = inner.redirect_url || inner.sign_url;
        if (u2) return u2;
      }
      return payload.redirect_url || payload.sign_url || '';
    },

    async ensureExecListForItr(placement_type_id, work_set_check_group_id, work_sets = null) {
      if (!this.data.projectId) {
        this.showErrorSnackbar('Не указан проект')
        return false
      }
      const row = await validateExecListBeforeOkk(this.data?.floor_map_id, {
        project_id: this.data?.projectId,
        placement_type_id,
        work_set_check_group_id,
        ...this.data?.documentParams,
      })
      if (row == null) {
        this.showErrorSnackbar('Не удалось проверить исполнительную схему')
        return false
      }
      if (!row.check_applies) {
        return true
      }
      if (!row.has_document) {
        const rows = Array.isArray(work_sets) ? work_sets : []
        if (!rows.length) {
          this.showErrorSnackbar('Нет работ в группе для исполнительной схемы')
          return false
        }
        this.showSecondaryModal('ExecListCreateDocument', {
          title: 'Исполнительная схема: объёмы работ',
          wide: true,
          projectId: this.data?.projectId,
          floorMapId: this.data?.floor_map_id,
          placementTypeId: placement_type_id,
          workSetCheckGroupId: work_set_check_group_id,
          rows: rows.map((s) => ({
            work_set_id: s.work_set_id,
            work_set_name: s.work_set_name,
            unit_name: s.unit_name,
            work_set_amount: s.work_set_amount,
          })),
          documentParams: this.documentParams || {},
          confirmContext: 'secondary',
          openConfirmModal: (payload) =>
            this.showSecondaryModal('ConfirmModal', payload),
          onSuccess: () => {
            if (this.reloadData) this.reloadData()
          },
        })
        return false
      }
      if (!row.has_pdf) {
        let pdfMessage =
          'Файл исполнительного листа ещё формируется. Дождитесь появления PDF в разделе «Документы», затем подпишите его и повторите вызов ИТР.'
        if (row.document_in_queue) {
          pdfMessage =
            'Документ уже в очереди на формирование PDF. Дождитесь появления файла в разделе «Документы», затем подпишите его и повторите вызов ИТР.'
        } else if (row.queued_for_generation) {
          pdfMessage =
            'Документ поставлен в очередь на формирование PDF. Дождитесь появления файла в разделе «Документы», затем подпишите его и повторите вызов ИТР.'
        }
        this.showSecondaryModal('ConfirmModal', {
          title: 'Исполнительная схема',
          message: pdfMessage,
          btnText: 'Понятно',
          cancelBtnText: 'Закрыть',
          onSubmit: async () => {
            this.closeModal()
          },
        })
        return false
      }
      if (!row.is_signed) {
        this.showSecondaryModal('ConfirmModal', {
          title: 'Подписание исполнительной схемы',
          message:
            'Исполнительный лист сформирован. Подпишите документ через NCA. После подписания снова нажмите «Вызов ИТР».',
          btnText: 'Подписать',
          cancelBtnText: 'Закрыть',
          extraBtnText: 'Скачать документ',
          onExtra: async () => {
            const master_url = row.file_url
            if (!master_url) {
              this.showErrorSnackbar('Нет ссылки на файл для скачивания')
              return
            }
            const title =
              `${row.floor_map_document_type_name || 'Исполнительная схема'}` +
              (row.document_date ? ` - ${row.document_date}` : '')
            await downloadFile(
              master_url,
              title,
              { ...(this.documentParams || {}) },
              row.file_ext || 'pdf',
              'get',
              false
            )
          },
          onSubmit: async () => {
            const floor_map_document_id = row.floor_map_document_id
            if (!floor_map_document_id) {
              this.showErrorSnackbar('Не найден документ для подписи')
              this.closeModal()
              return
            }
            const res = await signEntranceDocument(
              { floor_map_document_id },
              this.documentParams || {}
            )
            if (!res) return
            const redirectUrl = res.redirect_url || res.data?.redirect_url
            if (redirectUrl) {
              window.location.href = redirectUrl
              this.showSucceedSnackbar('Открывается подписание в NCA')
            } else {
              this.showErrorSnackbar('Не получен адрес подписания')
            }
          },
        })
        return false
      }
      return true
    },
    async onSignClick(row) {
      const floor_map_id = this.data?.floor_map_id;
      const placement_type_id = this.data?.placement_type_id;
      const work_set_check_group_id = this.data?.work_set_check_group_id;
      if (!floor_map_id || placement_type_id == null || work_set_check_group_id == null) {
        this.showErrorSnackbar('Не хватает данных для подписания');
        return;
      }

      const isExecList = row.type === 'exec_list_contractor' || row.type === 'exec_list_geo';
      if (isExecList) {
        this.signLoading = true;
        const execListRes = await this.ensureExecListForItr(
          placement_type_id,
          work_set_check_group_id,
          this.data.work_sets
        );
        if (!execListRes) {
          this.signLoading = false;
          return;
        }
        return;
      }
      const params = {
        ...(this.data.documentParams || {}),
        floor_map_id,
      };
      const body = {
        floor_map_id,
        placement_type_id,
        work_set_check_group_id,
        return_url: typeof window !== 'undefined' ? window.location.href : null,
      };
      // this.signLoading = true;
      // const res = await signAsrPreCall(body, params);
      // this.signLoading = false;
      if (!res) {
        this.showErrorSnackbar('Не удалось начать подписание');
        return;
      }
      if (res.success === false || res.status === false) {
        this.showErrorSnackbar(
          String(res.message || res.error || 'Ошибка подписания')
        );
        return;
      }
      const redirectUrl = this.pickSignRedirectUrl(res);
      if (!redirectUrl) {
        this.showErrorSnackbar('Не получен адрес для подписания');
        return;
      }
      this.closeModal();
      window.location.href = redirectUrl;
    },
  },
  setup() {
    const appStore = useAppStore();
    const { showSecondaryModal } = appStore;
    const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar();
    return { showErrorSnackbar, showSecondaryModal, showSucceedSnackbar };
  },
};
</script>

<style scoped lang="scss">
.pre-nu-call-steps {
  min-width: 320px;
  max-width: 920px;

  &__headrow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
  }

  &__intro {
    margin: 0;
    flex: 1;
    min-width: 0;
    font-size: 14px;
    color: #444;
    line-height: 1.4;
  }

  &__head-btn {
    flex-shrink: 0;
    margin-left: auto;
  }

  &__empty {
    padding: 16px;
    text-align: center;
    color: #888;
  }

  &__table {
    th {
      font-weight: 600;
      color: #333;
      background-color: #f5f5f5;
    }

    td {
      vertical-align: top;
    }
  }

  &__row--done td {
    background-color: #e8f5e9;
  }

  &__name {
    font-weight: 500;
    color: #2c2c2c;
  }

  &__info {
    color: #555;
    white-space: pre-line;
  }
}
</style>
